import React from 'react';
import { Building2, Loader2, Plus } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { companyCategories, typsaOrgCategories } from './activityConfig';

const ActivityCompanySelector = ({
  companyCategory,
  typsaOrgCategory,
  companyId,
  typsaOrgUnitId,
  companies,
  typsaOrgUnits,
  showCreateCompany,
  newCompanyName,
  creatingCompany,
  onCompanyCategoryChange,
  onTypsaOrgCategoryChange,
  onCompanyChange,
  onTypsaOrgUnitChange,
  onShowCreateCompanyChange,
  onNewCompanyNameChange,
  onCreateCompany
}) => {
  // Filter companies by type
  const getFilteredCompanies = () => {
    if (!companyCategory || companyCategory === 'typsa') return [];
    return companies.filter(c => c.company_type === companyCategory);
  };

  // Filter TYPSA units by category
  const getFilteredTypsaUnits = () => {
    if (!typsaOrgCategory) return [];
    const normalizedCategory = typsaOrgCategory.toLowerCase().trim();
    return typsaOrgUnits.filter(u => 
      u.category && u.category.toLowerCase().trim() === normalizedCategory
    );
  };

  const filteredCompanies = getFilteredCompanies();
  const filteredTypsaUnits = getFilteredTypsaUnits();

  return (
    <div className="space-y-3 p-3 border rounded-md bg-muted/20">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Building2 className="w-4 h-4" />
        Empresa / Unidad TYPSA
      </div>

      {/* Company Category Selection */}
      <div>
        <Label className="text-xs text-muted-foreground">Tipo</Label>
        <Select
          value={companyCategory || "none"}
          onValueChange={(value) => onCompanyCategoryChange(value === "none" ? '' : value)}
        >
          <SelectTrigger data-testid="company-category-select">
            <SelectValue placeholder="Seleccionar tipo..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-- Seleccionar --</SelectItem>
            {companyCategories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* TYPSA Category (if TYPSA selected) */}
      {companyCategory === 'typsa' && (
        <div>
          <Label className="text-xs text-muted-foreground">Categoría TYPSA</Label>
          <Select
            value={typsaOrgCategory || "none"}
            onValueChange={(value) => onTypsaOrgCategoryChange(value === "none" ? '' : value)}
          >
            <SelectTrigger data-testid="typsa-category-select">
              <SelectValue placeholder="Seleccionar categoría..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seleccionar --</SelectItem>
              {typsaOrgCategories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* TYPSA Unit (if TYPSA and category selected) */}
      {companyCategory === 'typsa' && typsaOrgCategory && (
        <div>
          <Label className="text-xs text-muted-foreground">Unidad TYPSA</Label>
          <Select
            value={typsaOrgUnitId || "none"}
            onValueChange={(value) => onTypsaOrgUnitChange(value === "none" ? '' : value)}
          >
            <SelectTrigger data-testid="typsa-unit-select">
              <SelectValue placeholder="Seleccionar unidad..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seleccionar --</SelectItem>
              {filteredTypsaUnits.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.value || unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Company Selection (if not TYPSA) */}
      {companyCategory && companyCategory !== 'typsa' && !showCreateCompany && (
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Empresa</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onShowCreateCompanyChange(true)}
              className="h-6 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Nueva
            </Button>
          </div>
          <Select
            value={companyId || "none"}
            onValueChange={(value) => onCompanyChange(value === "none" ? '' : value)}
          >
            <SelectTrigger data-testid="company-select">
              <SelectValue placeholder="Seleccionar empresa..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seleccionar --</SelectItem>
              {filteredCompanies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Quick Create Company */}
      {showCreateCompany && companyCategory !== 'typsa' && (
        <div className="space-y-2 p-2 border rounded bg-background">
          <Label className="text-xs">Nueva empresa ({companyCategory})</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Nombre de la empresa"
              value={newCompanyName}
              onChange={(e) => onNewCompanyNameChange(e.target.value)}
              data-testid="new-company-name"
            />
            <Button
              type="button"
              size="sm"
              onClick={onCreateCompany}
              disabled={creatingCompany || !newCompanyName.trim()}
            >
              {creatingCompany ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Crear'
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onShowCreateCompanyChange(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityCompanySelector;
